import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/system';
import { DialogCloseButton } from './dialogCloseButton';

type GenericDialogProps = {
  open: boolean;
  dialogTitle?: string | JSX.Element;
  dialogSubTitle?: string | JSX.Element;
  dialogTitleContent?: JSX.Element;
  dialogContent: JSX.Element;
  dialogActions?: JSX.Element;
  abortLabel?: string;
  saveLabel?: string;
  padding?: number;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  showDividers?: boolean;
  onClose: <T>(data?: T) => void;
  onSave: () => void;
  showDialogActions?: boolean;
  disableBackdropClosing?: boolean;
  titlePadding?: number;
  disableEnforceFocus?: boolean;
  hideBackdrop?: boolean;
};

export const GenericDialog = (props: GenericDialogProps) => {
  const {
    open,
    dialogTitle = null,
    dialogTitleContent = null,
    dialogSubTitle = null,
    dialogContent,
    dialogActions = null,
    abortLabel = 'button.cancel',
    saveLabel = 'button.save',
    padding = 2,
    showDividers = true,
    onClose,
    onSave,
    maxWidth = 'md',
    showDialogActions = true,
    disableBackdropClosing = false,
    titlePadding = 2,
    disableEnforceFocus = false,
    hideBackdrop = false,
  } = props;

  const { t } = useTranslation(['common']);

  const componentId = `dialog-${Date.now().toString(36) + Math.random().toString(36).substring(2)}`;

  const handleClose = () => {
    onClose();
  };

  const onHandleSave = () => {
    onSave();
  };

  return (
    <Dialog
      onClose={(_event, reason) => {
        if (disableBackdropClosing && reason && reason === 'backdropClick') return;
        handleClose();
      }}
      aria-labelledby={dialogTitle ? `${componentId}-titel` : undefined}
      open={open}
      maxWidth={maxWidth}
      fullWidth
      disableEnforceFocus={disableEnforceFocus}
      hideBackdrop={hideBackdrop}
      sx={{
        minWidth: {
          xs: '90vw',
          md: '400px',
        },
        '& .MuiDialogContent-root': {
          p: 2,
        },
        '& .MuiDialogActions-root': {
          p: 1,
        },
        '& .MuiDialogTitle-root': {
          p: titlePadding,
        },
      }}
    >
      {(dialogTitle || dialogTitleContent) && (
        <DialogTitle sx={{ m: 0, p: 2 }} id={`${componentId}-titel`}>
          {dialogTitleContent}
          {dialogTitle && (
            <Box sx={{ lineHeight: 1.2, fontWeight: 600, pr: 4 }}>
              {dialogTitle}
              {dialogSubTitle && (
                <Box component='small' sx={{ mt: 0.5, display: 'block', fontWeight: 200, color: 'text.secondary' }}>
                  {dialogSubTitle}
                </Box>
              )}
            </Box>
          )}

          <DialogCloseButton handleClose={handleClose} />
        </DialogTitle>
      )}
      {!dialogTitle && !dialogTitleContent && <DialogCloseButton handleClose={handleClose} />}
      <DialogContent dividers={showDividers} sx={{ p: `${padding} !important` }}>
        {dialogContent}
      </DialogContent>
      {showDialogActions && (
        <DialogActions>
          {dialogActions !== null && <>{dialogActions}</>}
          {!dialogActions && (
            <>
              <Button variant='outlined' color='primary' disableElevation onClick={handleClose}>
                {t(abortLabel ?? '')}
              </Button>
              <Button autoFocus variant='contained' color='primary' disableElevation onClick={onHandleSave}>
                {t(saveLabel ?? '')}
              </Button>
            </>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};
